import { FunctionComponent } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

import { ROUTE_CONFIG } from "@/routes/config";

import { formatDeletionDate, isDeletionOverdue } from "./deletionNotice";

/**
 * `ROUTE_CONFIG.SELECT_SUBSCRIPTION` — not the `/subscription` route nested in `Private.tsx` —
 * is the correct destination: it is the page every other "go buy a plan" CTA in the app already
 * links to (`UsageOverview`, `InviteSendingUserField`, the onboarding tour), and it is the
 * backend's actual Stripe redirect target for a returning customer (see `Private.tsx`'s
 * `ARCHIVED_ALLOWED_ROUTE_PREFIXES` comment) — so it is also the one route `Private.tsx` keeps
 * open while the rest of the app is walled off, which the `/subscription` page is not.
 */
const REACTIVATE_PATH = ROUTE_CONFIG.SELECT_SUBSCRIPTION;

/**
 * The full-page wall an archived company sees instead of the app (PREC-3900). Its whole job is
 * to name the deletion date and offer the way back — it renders in place of `Private.tsx`'s
 * `<Routes>` tree, not as one of the routes inside it, so every other route is unreachable while
 * this is on screen.
 */
export const ArchivedAccount: FunctionComponent<{ deletionAt: string | null }> = ({
	deletionAt,
}) => {
	// A past deletion date must not be announced as a deletion that already happened — see
	// `isDeletionOverdue`.
	const { t } = useTranslation();
	const ts = (key: string) => t(`archivedAccount.${key}`);

	const deletionDate = deletionAt ? formatDeletionDate(deletionAt) : null;
	const isOverdue = deletionAt ? isDeletionOverdue(deletionAt) : false;

	return (
		<div className="flex flex-col items-center justify-center min-h-screen px-6 text-center">
			<h1 className="text-md font-bold mb-4">{ts("title")}</h1>
			<p className="text-smd text-text-meta max-w-[520px] mb-2">{ts("description")}</p>
			{deletionDate ? (
				<p className="text-smd text-text-meta max-w-[520px] mb-8">
					<Trans
						components={{ 0: <b /> }}
						i18nKey={
							isOverdue ? "archivedAccount.deletionNoticeOverdue" : "archivedAccount.deletionNotice"
						}
						values={{ date: deletionDate }}
					/>
				</p>
			) : null}
			<Link
				className="appearance-none cursor-pointer text-center font-semibold rounded-[10px] w-fit border duration-200 bg-blue-action hover:bg-blue-action-dark hover:shadow-md text-white border-transparent h-[38px] px-4 flex items-center justify-center"
				to={REACTIVATE_PATH}
			>
				{ts("reactivateCta")}
			</Link>
		</div>
	);
};
